import type { ForwardDealProductBuilderRow, OTCForwardFieldsData } from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { FORWARD_AVAILABLE_UNDERLYING_SYMBOLS } from '@src/utils/OTC/otcUtils';
import { EMPTY_OBJECT, OTCSettlementTypeEnum, runValidationV1 } from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { object, string, type InferType } from 'yup-v1-latest';
import { applyForwardOtcFieldsUpdate } from './applyForwardOtcFieldsUpdate';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { FORWARD_PAYOFF_TYPES, OTC_SETTLEMENT_TYPES } from './tokens';
import { positiveNumericString } from './validationUtils';

export const ForwardProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<ForwardDealProductBuilderRow> = {
  getDefaultValues: (
    existingOTCFieldsForClear?: ForwardDealProductBuilderRow['otcFields']
  ): NonNullable<ForwardDealProductBuilderRow['otcFields']> => {
    const forwardDefaults: OTCForwardFieldsData = {
      productType: 'forward',
      quoteAsset: '',
      deliveryAsset: '',
      unitNotionalAmount: undefined,
      notionalAmount: undefined,
      payoffType: 'Linear',
      settlementType: OTCSettlementTypeEnum.Cash,
      index: '',
    };

    return { ...forwardDefaults, ...existingOTCFieldsForClear };
  },

  buildNoSuffixInstrumentName: (updatedRow: WritableDraft<ForwardDealProductBuilderRow>): string => {
    if (updatedRow && updatedRow.baseAsset && updatedRow.otcFields?.payoffType && updatedRow.otcFields?.quoteAsset) {
      // Include quote asset in instrument name if available
      const tenor = updatedRow.otcFields.tenor;
      let instrument = `FWD-${updatedRow.baseAsset}-${updatedRow.otcFields.quoteAsset}`;
      if (tenor) {
        instrument += `-${tenor}`;
      }
      instrument += `-${updatedRow.otcFields.payoffType.toUpperCase()}`;
      return instrument;
    }
    return '';
  },

  updateGridRowData: (
    state: WritableDraft<ForwardDealProductBuilderRow>,
    rowData: ForwardDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    const previousBaseAsset = state.baseAsset;
    state.baseAsset = rowData.baseAsset;
    state.productTemplate = rowData.productTemplate;
    state.uniqueSuffix = rowData.uniqueSuffix;

    const baseAssetChanged = previousBaseAsset !== rowData.baseAsset;

    // When underlying changes for Inverse payoff, set delivery asset to underlying
    if (state.otcFields?.payoffType === 'Inverse' && baseAssetChanged) {
      state.otcFields.deliveryAsset = rowData.baseAsset || undefined;
    }

    // When baseAsset changes, update index if current index is invalid for new baseAsset-quoteAsset pair
    if (baseAssetChanged && state.baseAsset && state.otcFields?.quoteAsset && fixingIndexMapping) {
      // Normalize quote asset for USD/USDT/USDC
      const normalizedQuoteAsset =
        state.otcFields.quoteAsset === 'USDT' || state.otcFields.quoteAsset === 'USDC'
          ? 'USD'
          : state.otcFields.quoteAsset;

      // Get available indexes for the new baseAsset-quoteAsset pair
      const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
        state.baseAsset,
        normalizedQuoteAsset,
        fixingIndexMapping
      );
      const availableIndexes = fixings
        .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
        .map(fixing => fixing.Name);

      if (availableIndexes.length > 0) {
        const currentIndex = state.otcFields.index;
        const currentIndexIsValid = currentIndex && availableIndexes.includes(currentIndex);

        // If current index is invalid or missing, select the first available (or single if only one)
        if (!currentIndexIsValid) {
          state.otcFields.index = availableIndexes[0];
        }
      } else {
        // No indexes available, clear it (validation will catch this)
        state.otcFields.index = '';
      }
    }
  },

  updateOTCFields: (
    state: WritableDraft<ForwardDealProductBuilderRow>,
    otcFields: ForwardDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    applyForwardOtcFieldsUpdate(state, otcFields, fixingIndexMapping);
  },

  validateFormState: (
    currentRow: ForwardDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    const schema = fixingIndexMapping
      ? createForwardProductBuilderSchema(fixingIndexMapping)
      : forwardProductBuilderSchema;
    return runValidationV1(schema, currentRow, { multipleErrorMode: 'inline' });
  },

  validateOTCFields: (
    otcFields: ForwardDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    return runValidationV1(forwardOtcFieldsSchema, otcFields ?? EMPTY_OBJECT, { multipleErrorMode: 'inline' });
  },
};

/** UI-side validation schema for forward OTC Fields form */
export const forwardOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['forward'] as const)
    .required('Product type is required'),
  quoteAsset: string().required('Quote asset is required'),
  // deliveryAsset is optional for all payoff types:
  // - For Inverse: it's derived from baseAsset
  // - For Linear: quoteAsset serves as default when deliveryAsset is not set
  deliveryAsset: string().optional(),
  unitNotionalAmount: positiveNumericString('Unit notional amount'),
  payoffType: string()
    .required('Payoff type is required')
    .oneOf(FORWARD_PAYOFF_TYPES, 'Payoff type must be Linear or Inverse'),
  settlementType: string()
    .required('Settlement type is required')
    .oneOf(OTC_SETTLEMENT_TYPES, `Settlement type must be one of: ${OTC_SETTLEMENT_TYPES.join(', ')}`),
  tenor: string().optional(),
  index: string().required('Index is required'),
});

export const createForwardProductBuilderSchema = (fixingIndexMapping?: FixingIndexMappingMap) =>
  object({
    productTemplate: string().required('Product Template is required'),
    product: string()
      .oneOf(['forward'] as const)
      .required('Product is required'),
    baseAsset: string()
      .required('Underlying is required')
      .oneOf([...FORWARD_AVAILABLE_UNDERLYING_SYMBOLS], 'Invalid underlying symbol'),
    uniqueSuffix: string().nullable(),
    otcFields: forwardOtcFieldsSchema
      .required('OTC fields are required')
      .test(
        'index-valid-for-pair',
        'Index must be valid for the selected Underlying and Quote Asset pair',
        function (otcFields) {
          if (!otcFields || !this.parent.baseAsset || !otcFields.quoteAsset || !otcFields.index) {
            return true;
          }

          const normalizedQuoteAsset =
            otcFields.quoteAsset === 'USDT' || otcFields.quoteAsset === 'USDC' ? 'USD' : otcFields.quoteAsset;

          if (fixingIndexMapping && fixingIndexMapping.size > 0) {
            const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
              this.parent.baseAsset,
              normalizedQuoteAsset,
              fixingIndexMapping
            );
            const availableIndexes = fixings
              .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
              .map(fixing => fixing.Name);
            return availableIndexes.includes(otcFields.index);
          }

          return true;
        }
      ),
  });

export const forwardProductBuilderSchema = createForwardProductBuilderSchema();

export type ValidatedForwardRowData = InferType<typeof forwardProductBuilderSchema>;
